import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, Button, Input, PageHeader } from '../components/ui';
import {
  Plus,
  Building2,
  MapPin,
  Star,
  MessageSquare,
  MoreVertical,
  ExternalLink,
  Pencil,
  Trash2,
  X,
  Save,
  Loader2,
  AlertCircle,
  Sparkles,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { businessApi, type Business, type CreateBusinessInput } from '../lib/api';
import { getBusinessList } from '../lib/businessListCache';

const TONE_OPTIONS = ['официальный', 'дружелюбный', 'экспертный'];
const EMPTY_BUSINESS_FORM: CreateBusinessInput & { advantagesText?: string } = {
  name: '',
  category: '',
  city: '',
  websiteUrl: '',
  description: '',
  utp: '',
  yandexMapsUrl: '',
  twogisUrl: '',
  targetAudience: '',
  tone: 'дружелюбный',
  advantages: [],
  advantagesText: '',
};

export function Businesses() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingBusiness, setEditingBusiness] = useState<Business | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isGeneratingBrief, setIsGeneratingBrief] = useState(false);
  const [showBriefSection, setShowBriefSection] = useState(false);

  // Form state
  const [formData, setFormData] = useState<CreateBusinessInput & { advantagesText?: string }>(EMPTY_BUSINESS_FORM);

  useEffect(() => {
    loadBusinesses();
  }, []);

  useEffect(() => {
    if (searchParams.get('new') !== '1') return;
    setEditingBusiness(null);
    setFormData(EMPTY_BUSINESS_FORM);
    setShowBriefSection(false);
    setShowForm(true);
  }, [searchParams]);

  const loadBusinesses = async () => {
    try {
      setIsLoading(true);
      const data = await getBusinessList();
      setBusinesses(data);
    } catch (err) {
      setError('Не удалось загрузить список бизнесов');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: keyof typeof formData, value: string | string[]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleNewBusiness = () => {
    setEditingBusiness(null);
    setFormData(EMPTY_BUSINESS_FORM);
    setShowBriefSection(false);
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    if (searchParams.get('new') === '1') {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete('new');
        return next;
      });
    }
  };

  const handleEditBusiness = (business: Business) => {
    setEditingBusiness(business);
    setFormData({
      name: business.name,
      category: business.category,
      city: business.city,
      websiteUrl: business.websiteUrl || '',
      description: business.description || '',
      utp: business.utp || '',
      yandexMapsUrl: business.yandexMapsUrl || '',
      twogisUrl: business.twogisUrl || '',
      targetAudience: business.targetAudience || '',
      tone: business.tone || 'дружелюбный',
      advantages: business.advantages || [],
      advantagesText: (business.advantages || []).join('\n'),
    });
    setShowBriefSection(!!(business.targetAudience || business.tone || business.advantages?.length));
    setShowForm(true);
    setActiveMenu(null);
  };

  const handleSave = async () => {
    if (!formData.name || !formData.category || !formData.city) {
      setError('Заполните обязательные поля: название, категория и город');
      return;
    }

    try {
      setIsSaving(true);
      setError(null);

      const advantages = formData.advantagesText
        ? formData.advantagesText.split('\n').map((s) => s.trim()).filter(Boolean)
        : formData.advantages || [];

      const payload: CreateBusinessInput = {
        name: formData.name,
        category: formData.category,
        city: formData.city,
        websiteUrl: formData.websiteUrl,
        description: formData.description,
        utp: formData.utp,
        yandexMapsUrl: formData.yandexMapsUrl,
        twogisUrl: formData.twogisUrl,
        targetAudience: formData.targetAudience,
        tone: formData.tone,
        advantages,
      };

      if (editingBusiness) {
        await businessApi.update(editingBusiness.id, payload);
      } else {
        await businessApi.create(payload);
      }

      await loadBusinesses();
      setShowForm(false);
      setEditingBusiness(null);
    } catch (err) {
      setError('Не удалось сохранить бизнес');
      console.error(err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleGenerateBrief = async () => {
    if (!editingBusiness) return;

    try {
      setIsGeneratingBrief(true);
      setError(null);

      const response = await businessApi.generateBrief(editingBusiness.id);
      const updated = response.data.data;

      setFormData((prev) => ({
        ...prev,
        targetAudience: updated.targetAudience || prev.targetAudience,
        tone: updated.tone || prev.tone,
        advantages: updated.advantages || prev.advantages,
        advantagesText: (updated.advantages || []).join('\n'),
      }));
    } catch (err) {
      setError('Не удалось сгенерировать бриф');
      console.error(err);
    } finally {
      setIsGeneratingBrief(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Удалить этот бизнес? Все связанные данные (отчёты, отзывы, услуги) также будут удалены.')) {
      return;
    }

    try {
      await businessApi.delete(id);
      await loadBusinesses();
      setActiveMenu(null);
    } catch (err) {
      setError('Не удалось удалить бизнес');
      console.error(err);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Бизнесы"
        subtitle="Управление вашими бизнесами на Яндекс.Картах"
      />

      <div className="p-6">
        {/* Error message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
            <AlertCircle className="w-5 h-5" />
            {error}
            <button onClick={() => setError(null)} className="ml-auto text-red-500 hover:text-red-700">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Form modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 overflow-y-auto py-6">
            <Card className="w-full max-w-lg mx-4 my-auto">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold">
                  {editingBusiness ? 'Редактировать бизнес' : 'Новый бизнес'}
                </h2>
                <button
                  onClick={closeForm}
                  className="p-1 hover:bg-gray-100 rounded"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4">
                {/* Основные поля */}
                <Input
                  label="Название *"
                  placeholder='Например: Клиника "Здоровье"'
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                />
                <Input
                  label="Категория *"
                  placeholder="Например: Медицинская клиника"
                  value={formData.category}
                  onChange={(e) => handleInputChange('category', e.target.value)}
                />
                <Input
                  label="Город *"
                  placeholder="Например: Москва"
                  value={formData.city}
                  onChange={(e) => handleInputChange('city', e.target.value)}
                />
                <Input
                  label="Сайт компании"
                  placeholder="https://example.ru"
                  value={formData.websiteUrl}
                  onChange={(e) => handleInputChange('websiteUrl', e.target.value)}
                />
                <Input
                  label="Ссылка на Яндекс.Карты"
                  placeholder="https://yandex.ru/maps/org/..."
                  value={formData.yandexMapsUrl}
                  onChange={(e) => handleInputChange('yandexMapsUrl', e.target.value)}
                />
                <Input
                  label="Ссылка на 2ГИС"
                  placeholder="https://2gis.ru/.../firm/..."
                  value={formData.twogisUrl}
                  onChange={(e) => handleInputChange('twogisUrl', e.target.value)}
                />
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Описание</label>
                  <textarea
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    rows={2}
                    placeholder="Краткое описание бизнеса..."
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                  />
                </div>
                <Input
                  label="УТП"
                  placeholder="Уникальное торговое предложение..."
                  value={formData.utp}
                  onChange={(e) => handleInputChange('utp', e.target.value)}
                />

                {/* Бриф для AI — раскрывающаяся секция */}
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setShowBriefSection(!showBriefSection)}
                    className="w-full flex items-center justify-between px-4 py-3 bg-purple-50 text-purple-700 hover:bg-purple-100 transition-colors"
                  >
                    <span className="flex items-center gap-2 font-medium text-sm">
                      <Sparkles className="w-4 h-4" />
                      Бриф для AI-генерации
                    </span>
                    {showBriefSection ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>

                  {showBriefSection && (
                    <div className="p-4 space-y-4">
                      {editingBusiness && (
                        <Button
                          variant="outline"
                          size="sm"
                          icon={isGeneratingBrief ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                          onClick={handleGenerateBrief}
                          loading={isGeneratingBrief}
                          className="w-full border-purple-300 text-purple-700 hover:bg-purple-50"
                        >
                          Сгенерировать бриф автоматически
                        </Button>
                      )}

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Целевая аудитория</label>
                        <textarea
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
                          rows={3}
                          placeholder="Опишите вашу целевую аудиторию: возраст, интересы, боли..."
                          value={formData.targetAudience}
                          onChange={(e) => handleInputChange('targetAudience', e.target.value)}
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Тон коммуникации</label>
                        <select
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
                          value={formData.tone}
                          onChange={(e) => handleInputChange('tone', e.target.value)}
                        >
                          {TONE_OPTIONS.map((t) => (
                            <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          УТП и преимущества (каждое с новой строки)
                        </label>
                        <textarea
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
                          rows={4}
                          placeholder="Опытные специалисты&#10;Современное оборудование&#10;Гибкий график работы"
                          value={formData.advantagesText}
                          onChange={(e) => handleInputChange('advantagesText', e.target.value)}
                        />
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex gap-3 pt-4 border-t">
                  <Button onClick={handleSave} loading={isSaving} icon={<Save className="w-4 h-4" />}>
                    {editingBusiness ? 'Сохранить' : 'Создать'}
                  </Button>
                  <Button variant="outline" onClick={closeForm}>
                    Отмена
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-between items-center mb-6">
          <p className="text-gray-600">
            Всего: <span className="font-medium">{businesses.length}</span> бизнеса
          </p>
          <Button icon={<Plus className="w-4 h-4" />} onClick={handleNewBusiness}>
            Добавить бизнес
          </Button>
        </div>

        {/* Business cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {businesses.map((business) => (
            <Card key={business.id} className="relative group" padding="none">
              {/* Header */}
              <div className="p-4 border-b border-gray-100">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                      <Building2 className="w-6 h-6 text-primary-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{business.name}</h3>
                      <p className="text-sm text-gray-500">{business.category}</p>
                    </div>
                  </div>

                  {/* Menu */}
                  <div className="relative">
                    <button
                      onClick={() => setActiveMenu(activeMenu === business.id ? null : business.id)}
                      className="p-1 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      <MoreVertical className="w-5 h-5 text-gray-400" />
                    </button>
                    {activeMenu === business.id && (
                      <div className="absolute right-0 top-full mt-1 w-40 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10">
                        <button
                          onClick={() => handleEditBusiness(business)}
                          className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                        >
                          <Pencil className="w-4 h-4" />
                          Редактировать
                        </button>
                        <button
                          onClick={() => handleDelete(business.id)}
                          className="flex items-center gap-2 w-full px-3 py-2 text-sm text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                          Удалить
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Stats */}
              <div className="p-4 space-y-3">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <MapPin className="w-4 h-4" />
                  {business.city}
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1">
                    <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                    <span className="font-medium">{(business.averageRating || 0).toFixed(1)}</span>
                  </div>
                  <div className="flex items-center gap-1 text-gray-500">
                    <MessageSquare className="w-4 h-4" />
                    <span className="text-sm">{business.reviewsCount || 0} отзывов</span>
                  </div>
                </div>

                <div className="flex items-center justify-between text-sm text-gray-500">
                  <span>{business.servicesCount || 0} услуг в каталоге</span>
                  {business.tone && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-50 text-purple-600 rounded-full text-xs">
                      <Sparkles className="w-3 h-3" />
                      {business.tone}
                    </span>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="p-4 pt-0 flex gap-2">
                <Button variant="outline" size="sm" className="flex-1" onClick={() => navigate(`/client/${business.id}/dashboard`)}>
                  Открыть
                </Button>
                {business.yandexMapsUrl && (
                  <a
                    href={business.yandexMapsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </a>
                )}
              </div>
            </Card>
          ))}

          {/* Add new business card */}
          <button
            onClick={handleNewBusiness}
            className="border-2 border-dashed border-gray-200 rounded-xl p-6 flex flex-col items-center justify-center gap-3 text-gray-400 hover:border-primary-300 hover:text-primary-600 transition-colors min-h-[250px]"
          >
            <div className="w-12 h-12 rounded-full border-2 border-current flex items-center justify-center">
              <Plus className="w-6 h-6" />
            </div>
            <span className="font-medium">Добавить бизнес</span>
          </button>
        </div>
      </div>
    </div>
  );
}
